import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Check, ChevronDown, ChevronUp, Tablet, Trash2, Pencil,
  X, Plus, Clock, Minimize2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { decimalToHMS } from "@/lib/time";
import { SOURCE_LABELS } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useFunder } from "@/funder/FunderContext";
import { Button } from "@/components/ui-kit/Button";
import { Input } from "@/components/ui-kit/Input";
import { Select } from "@/components/ui-kit/Select";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { Pill } from "@/components/ui-kit/Pill";

const dstr = (d) => d.toISOString().slice(0, 10);
const r1 = (v) => Math.round(Number(v || 0) * 10) / 10;

function FieldLabel({ children }) {
  return <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{children}</label>;
}

/** Inline-editable quick-add preset boxes. Tap to apply to Hours; Edit to change/persist. */
function EditablePresets({ presets, onApply, onSave, big }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(presets);

  if (!editing) {
    return (
      <div className="flex flex-wrap items-center gap-1.5" data-testid="quick-add-presets">
        <span className="text-xs font-medium text-muted-foreground">Quick add:</span>
        {presets.map((h, i) => (
          <button
            key={`${h}-${i}`}
            type="button"
            data-testid={`quick-add-${h}`}
            onClick={() => onApply(h)}
            className={cn("rounded-md border border-border bg-surface font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground", big ? "px-4 py-2 text-base" : "px-2.5 py-1 text-sm")}
          >
            +{h}
          </button>
        ))}
        <button type="button" data-testid="edit-presets" onClick={() => { setDraft(presets); setEditing(true); }} className="ml-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="quick-add-editor">
      <span className="text-xs font-medium text-muted-foreground">Quick add:</span>
      {draft.map((h, i) => (
        <span key={i} className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-surface px-1.5 py-1">
          <span className="text-xs text-muted-foreground">+</span>
          <input
            type="number"
            step="0.1"
            min="0"
            value={h}
            data-testid={`quick-add-input-${i}`}
            onChange={(e) => setDraft(draft.map((x, idx) => (idx === i ? e.target.value : x)))}
            className="w-12 bg-transparent text-sm nums outline-none"
          />
          <button type="button" data-testid={`quick-add-remove-${i}`} onClick={() => setDraft(draft.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive">
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
      <button type="button" data-testid="quick-add-new" onClick={() => setDraft([...draft, "0.1"])} className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground">
        <Plus className="h-3.5 w-3.5" /> Add
      </button>
      <button
        type="button"
        data-testid="quick-add-save"
        onClick={() => { onSave(draft.map(Number).filter((v) => v > 0)); setEditing(false); }}
        className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
      >
        Done
      </button>
    </div>
  );
}

/** Inline-editable default time segment per activity template (persists on blur). */
function ActivityTimesEditor({ templates, open, onToggle, onSaveTime }) {
  return (
    <div data-testid="activity-times-editor">
      <button type="button" data-testid="edit-activity-times" onClick={onToggle} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <Clock className="h-3.5 w-3.5" /> {open ? "Hide" : "Edit"} activity times
      </button>
      {open && (
        <div className="mt-2 grid gap-1.5 rounded-md border border-border bg-secondary/40 p-2 sm:grid-cols-2">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center gap-2" data-testid={`activity-time-row-${t.id}`}>
              <span className="flex-1 truncate text-sm text-foreground">{t.label}</span>
              <input
                type="number"
                step="0.1"
                min="0"
                defaultValue={t.default_hours ?? ""}
                placeholder="none"
                data-testid={`activity-time-input-${t.id}`}
                onBlur={(e) => onSaveTime(t.id, e.target.value)}
                className="w-16 rounded border border-border bg-surface px-2 py-1 text-sm nums"
              />
              <span className="text-xs text-muted-foreground">h</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function QuickTimeLogPage() {
  const navigate = useNavigate();
  const { typeLabel } = useFunder();
  const clientRef = useRef(null);
  const [tablet, setTablet] = useState(false);
  const [board, setBoard] = useState([]);
  const [presets, setPresets] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [date, setDate] = useState(dstr(new Date()));
  const [clientId, setClientId] = useState("");
  const [hours, setHours] = useState("");
  const [labels, setLabels] = useState([]);
  const [description, setDescription] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [activityEditOpen, setActivityEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [error, setError] = useState("");
  const [log, setLog] = useState([]);

  const loadBoard = () => api.get("/board").then((r) => setBoard(r.data)).catch(() => {});
  const loadLog = (d) => api.get(`/time-entries?date=${d}`).then((r) => setLog(r.data)).catch(() => {});

  useEffect(() => {
    loadBoard();
    api.get("/settings").then((r) => setPresets(r.data.quick_hour_presets || [])).catch(() => {});
    api.get("/activity-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadLog(date); }, [date]);

  // Tablet mode fills the entire screen — lock body scroll behind the overlay.
  useEffect(() => {
    if (!tablet) return undefined;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [tablet]);

  const activeRow = board.find((rr) => rr.client.id === clientId && rr.has_active);
  const matter = activeRow?.matter;

  const addLabel = (label) => {
    if (!label || labels.includes(label)) return;
    const next = [...labels, label];
    setLabels(next);
    // First label on an empty-duration entry auto-fills its default hours
    // (matches the Workbench). Additional labels never stack duration, and
    // labels never touch the free-text description.
    if (next.length === 1) {
      const t = templates.find((x) => x.label === label);
      if (t && t.default_hours != null && !hours) setHours(String(t.default_hours));
    }
  };
  const removeLabel = (label) => setLabels((prev) => prev.filter((l) => l !== label));
  const applyPreset = (h) => setHours(String(r1(Number(hours || 0) + h)));
  const savePresets = async (next) => {
    setPresets(next);
    await api.put("/settings", { quick_hour_presets: next }).catch(() => {});
  };
  const saveActivityTime = async (id, val) => {
    const dh = val === "" ? null : Number(val);
    await api.patch(`/activity-templates/${id}`, { default_hours: dh }).catch(() => {});
    setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, default_hours: dh } : t)));
  };

  const save = async () => {
    if (!clientId || !matter) { setError("Pick a client with an active matter."); return; }
    if (!(Number(hours) > 0)) { setError("Enter hours greater than 0."); return; }
    setSaving(true);
    setError("");
    const { hours: h, minutes: m, seconds: s } = decimalToHMS(Number(hours));
    const desc = [description.trim(), notes.trim()].filter(Boolean).join(" — ");
    try {
      await api.post(`/matters/${matter.id}/time-entries`, {
        date: new Date(date).toISOString(),
        hours: h, minutes: m, seconds: s,
        // Keep the single `template` in sync for back-compat (first label wins),
        // exactly as the Workbench does. Labels are display-only enrichments.
        description: desc, template: labels[0] || "", labels, source: "QuickLog",
      });
      setSavedMsg(`Saved to ${matter.name}`);
      setTimeout(() => setSavedMsg(""), 2500);
      setClientId(""); setHours(""); setLabels([]); setDescription(""); setNotes(""); setNotesOpen(false);
      clientRef.current?.focus();
      loadLog(date);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => { await api.delete(`/time-entries/${id}`); loadLog(date); };

  const big = tablet;
  const ctl = big ? "h-14 text-lg" : "";

  const dateControls = (
    <div className="flex flex-wrap items-center gap-2">
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ql-date" className={cn("max-w-[190px]", ctl)} />
      <Button variant="secondary" size={big ? "lg" : "md"} onClick={() => setDate(dstr(new Date()))} data-testid="ql-today">Today</Button>
      <Button variant="secondary" size={big ? "lg" : "md"} onClick={() => setDate(dstr(new Date(Date.now() - 864e5)))} data-testid="ql-yesterday">Yesterday</Button>
    </div>
  );

  const entryForm = (
    <div className="space-y-3" data-testid="quick-log-form">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
        <div className="md:col-span-3">
          <FieldLabel>Client</FieldLabel>
          <Select ref={clientRef} value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="ql-client" className={ctl}>
            <option value="">Select client…</option>
            {board.map((rr) => (
              <option key={rr.client.id} value={rr.client.id} disabled={!rr.has_active}>
                {rr.client.name}{!rr.has_active ? " (no active matter)" : ""}
              </option>
            ))}
          </Select>
        </div>
        <div className="md:col-span-3">
          <FieldLabel>Matter</FieldLabel>
          <Input value={matter ? `${matter.name} · ${typeLabel(matter.type)}` : ""} readOnly data-testid="ql-matter" placeholder="Select a client first" className={cn("bg-secondary/40", ctl)} />
        </div>
        <div className="md:col-span-2">
          <FieldLabel>Hours</FieldLabel>
          <Input type="number" step="0.1" min="0" value={hours} onChange={(e) => setHours(e.target.value)} onFocus={(e) => e.target.select()} data-testid="ql-hours" className={cn("nums", ctl)} placeholder="0.0" />
        </div>
        <div className="md:col-span-2">
          <FieldLabel>Tasks</FieldLabel>
          <div className="flex flex-wrap gap-1" data-testid="ql-labels">
            {labels.map((l) => (
              <span
                key={l}
                data-testid={`ql-label-chip-${l}`}
                className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary"
              >
                {l}
                <button
                  type="button"
                  onClick={() => removeLabel(l)}
                  data-testid={`ql-label-remove-${l}`}
                  className="text-primary/70 hover:text-primary"
                  title="Remove task"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <Select
            value=""
            onChange={(e) => { addLabel(e.target.value); e.target.value = ""; }}
            data-testid="ql-activity"
            className={cn("mt-1", ctl)}
          >
            <option value="">+ Add task…</option>
            {templates
              .filter((t) => !labels.includes(t.label))
              .map((t) => (
                <option key={t.id} value={t.label}>{t.label}{t.default_hours != null ? ` (${t.default_hours}h)` : ""}</option>
              ))}
          </Select>
        </div>
        <div className="md:col-span-2">
          <FieldLabel>Description</FieldLabel>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="ql-description" placeholder="Brief description" className={ctl} />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <EditablePresets presets={presets} onApply={applyPreset} onSave={savePresets} big={big} />
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setNotesOpen((o) => !o)} data-testid="ql-notes-toggle" className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground">
            {notesOpen ? <ChevronUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />} Add notes
          </button>
          <Button onClick={save} disabled={saving} data-testid="ql-save" size={big ? "lg" : "md"}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
          </Button>
        </div>
      </div>

      <ActivityTimesEditor templates={templates} open={activityEditOpen} onToggle={() => setActivityEditOpen((o) => !o)} onSaveTime={saveActivityTime} />

      {notesOpen && <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ql-notes" placeholder="Additional notes…" className={ctl} />}

      {error && <p data-testid="ql-error" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
      <span data-testid="ql-saved" className={cn("inline-flex items-center gap-1.5 text-sm font-medium text-status-paid transition-opacity", savedMsg ? "opacity-100" : "opacity-0")}>
        <Check className="h-4 w-4" /> {savedMsg || "Saved"}
      </span>
    </div>
  );

  const todayLog = (
    <div data-testid="today-log">
      {log.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No entries logged for this date yet.</p>
      ) : (
        <Table>
          <THead><TR><TH>Time</TH><TH>Client</TH><TH>Matter</TH><TH className="text-right">Hours</TH><TH>Activity</TH><TH>Type</TH><TH>Review</TH><TH className="w-20 text-right">Actions</TH></TR></THead>
          <TBody>
            {log.map((e) => (
              <TR key={e.id} data-testid={`today-log-row-${e.id}`}>
                <TD className="whitespace-nowrap text-muted-foreground">{new Date(e.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</TD>
                <TD className="font-medium text-foreground">{e.client_name}</TD>
                <TD className="text-muted-foreground">{e.matter_name}</TD>
                <TD className="nums text-right">{e.decimal_hours}</TD>
                <TD className="text-muted-foreground">{e.template || e.description || "—"}</TD>
                <TD className="text-muted-foreground">{typeLabel(e.matter_type)}</TD>
                <TD><Pill tone={e.status === "Draft" ? "info" : e.status === "Excluded" ? "muted" : "success"}>{e.status === "Draft" ? "Needs Review" : e.status}</Pill></TD>
                <TD>
                  <div className="flex justify-end gap-1">
                    <button type="button" onClick={() => navigate(`/workbench/${e.matter_id}`)} data-testid={`today-log-edit-${e.id}`} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" title="Open in workbench"><Pencil className="h-4 w-4" /></button>
                    <button type="button" onClick={() => del(e.id)} data-testid={`today-log-delete-${e.id}`} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );

  if (tablet) {
    return createPortal(
      <div className="fixed inset-0 z-[70] flex flex-col overflow-y-auto bg-background" data-testid="quick-log-tablet-overlay">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-surface px-6 py-4">
          <h1 className="font-serif text-2xl font-semibold text-foreground">Quick Time Log</h1>
          <Button variant="secondary" size="lg" onClick={() => setTablet(false)} data-testid="tablet-toggle">
            <Minimize2 className="h-4 w-4" /> Exit Tablet Mode
          </Button>
        </div>
        <div className="mx-auto w-full max-w-6xl space-y-6 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Log for {date}</p>
            {dateControls}
          </div>
          <div className="rounded-lg border border-border bg-surface p-5 shadow-card">{entryForm}</div>
          <div className="rounded-lg border border-border bg-surface p-5 shadow-card">
            <p className="mb-3 text-sm font-semibold text-foreground">{log.length} {log.length === 1 ? "entry" : "entries"} on this day</p>
            {todayLog}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div className="space-y-6" data-testid="quick-log-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">Quick Time Log</h1>
          <p className="mt-2 text-sm text-muted-foreground">Fast daily log — client-first capture.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {dateControls}
          <button
            type="button"
            onClick={() => setTablet(true)}
            data-testid="tablet-toggle"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent"
          >
            <Tablet className="h-4 w-4" /> Tablet Mode
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-surface p-5 shadow-card">{entryForm}</div>

      <div className="rounded-lg border border-border bg-surface shadow-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-semibold text-foreground">{`Today's log — ${date}`}</h2>
          <span className="text-sm text-muted-foreground">{log.length} {log.length === 1 ? "entry" : "entries"} on this day</span>
        </div>
        <div className="p-5">{todayLog}</div>
      </div>
    </div>
  );
}
